import React from 'react';
import { Check, X } from 'lucide-react';
import { Button } from '../UI/Button';
import { Avatar } from '../UI/Avatar';
import { PERMISSION_LABELS } from '../../api/eventTeam';
import type { AppNotification, EventManagerPermissions } from '../../types';
import styles from './NotificationItem.module.css';

interface NotificationItemProps {
  notification: AppNotification;
  actioning: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function getGrantedLabels(perms: EventManagerPermissions): string[] {
  return (Object.entries(perms) as [keyof EventManagerPermissions, boolean][])
    .filter(([, v]) => v)
    .map(([k]) => PERMISSION_LABELS[k]);
}

export function NotificationItem({ notification, actioning, onAccept, onDecline }: NotificationItemProps) {
  if (notification.type === 'manager_invite') {
    const { data } = notification;
    const isActioned = notification.actionTaken !== null;
    const granted = getGrantedLabels(data.permissions);
    const isFullAccess = granted.length >= 8;
    const visible = isFullAccess ? [] : granted.slice(0, 2);
    const overflow = isFullAccess ? 0 : granted.length - visible.length;

    return (
      <div className={[styles.card, isActioned ? styles['card-actioned'] : ''].filter(Boolean).join(' ')}>

        {/* Meta row: category label + timestamp */}
        <div className={styles['meta-row']}>
          <span className={styles['invite-label']}>Invitation</span>
          <span className={styles.time}>{formatRelative(notification.createdAt)}</span>
        </div>

        {/* Inviter identity */}
        <div className={styles['inviter-row']}>
          <Avatar name={data.inviterName} src={data.inviterPicture} size="sm" />
          <div className={styles['inviter-text']}>
            <span className={styles['inviter-name']}>{data.inviterName}</span>
            <span className={styles['inviter-caption']}>invited you to manage</span>
          </div>
        </div>

        {/* Event name — dominant element */}
        <p className={styles['event-name']}>{data.eventName}</p>

        {/* Permissions */}
        <div className={styles['perms-section']}>
          <span className={styles['perms-heading']}>Can manage</span>
          {isFullAccess ? (
            <span className={styles['perms-full']}>Full access to the event</span>
          ) : granted.length === 0 ? (
            <span className={styles['perms-full']}>View-only access</span>
          ) : (
            <ul className={styles['perms-list']}>
              {visible.map((p) => (
                <li key={p} className={styles['perms-item']}>{p}</li>
              ))}
              {overflow > 0 && (
                <li className={styles['perms-overflow']}>+{overflow} more</li>
              )}
            </ul>
          )}
        </div>

        {/* CTA or actioned state */}
        {isActioned ? (
          <div
            className={[
              styles['actioned-pill'],
              notification.actionTaken === 'accept' ? styles['actioned-accept'] : styles['actioned-decline'],
            ].join(' ')}
          >
            {notification.actionTaken === 'accept' ? 'Accepted' : 'Declined'}
          </div>
        ) : (
          <div className={styles.actions}>
            <Button variant="ghost" size="sm" onClick={onDecline} disabled={actioning}>
              <X style={{ width: '0.8125rem', height: '0.8125rem', marginRight: '0.25rem' }} />
              Decline
            </Button>
            <Button variant="primary" size="sm" onClick={onAccept} loading={actioning}>
              <Check style={{ width: '0.8125rem', height: '0.8125rem', marginRight: '0.25rem' }} />
              Accept Invite
            </Button>
          </div>
        )}
      </div>
    );
  }

  return null;
}
